import { z } from 'zod';
// The model "move" loop (01-architecture.md): the extension sends a screenshot
// and the numbered interactive elements of the page; the API asks the
// navigation model and answers with exactly one move. The extension never
// calls a model directly.
export const pageElementSchema = z.object({
    /** The number drawn on the screenshot. */
    n: z.int().min(0),
    tag: z.string().max(32),
    role: z.string().max(32).nullable().optional(),
    text: z.string().max(200),
    value: z.string().max(200).nullable().optional(),
});
export const moveSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('click'), element: z.int().min(0), reason: z.string().optional() }),
    z.object({
        kind: z.literal('type'),
        element: z.int().min(0),
        text: z.string(),
        submit: z.boolean().optional(),
        reason: z.string().optional(),
    }),
    z.object({
        kind: z.literal('select'),
        element: z.int().min(0),
        value: z.string(),
        reason: z.string().optional(),
    }),
    z.object({ kind: z.literal('scroll'), direction: z.enum(['up', 'down']), reason: z.string().optional() }),
    /** The page is still loading (v1 `aspetta`). */
    z.object({ kind: z.literal('wait'), reason: z.string().optional() }),
    /** The results are on screen: capture now. */
    z.object({ kind: z.literal('done'), reason: z.string().optional() }),
    /**
     * The site searched and says the reference does not exist (v1 `vuoto`):
     * the recipe/model did its job, the number is wrong for this site.
     */
    z.object({ kind: z.literal('not_found'), reason: z.string() }),
    /** The model cannot go on (captcha, unknown page): a person is needed. */
    z.object({ kind: z.literal('give_up'), reason: z.string() }),
]);
/**
 * `search`: reaching the results; `results`: on the results, opening what is
 * still folded (details, expandable rows) before capturing (v1 `fase`).
 */
export const navigationPhaseSchema = z.enum(['search', 'results']);
/** `POST /extension/jobs/{job_id}/move`. */
export const moveRequestSchema = z.object({
    job_id: z.string(),
    /** The client that claimed the job. */
    client_name: z.string().min(1).max(32).optional(),
    phase: navigationPhaseSchema.default('search'),
    goal: z.string(),
    url: z.string(),
    screenshot_jpeg_base64: z.string(),
    elements: z.array(pageElementSchema).max(400),
    history: z.array(moveSchema).max(50),
    /** Visible text of the page (all frames), an excerpt. */
    page_text: z.string().max(20_000).optional(),
    /** How the last move ended when it went wrong («element 7 is covered»). */
    last_error: z.string().max(500).nullable().optional(),
    /** A warning the extension noticed (the same move repeated three times). */
    warning: z.string().max(500).nullable().optional(),
});
export const moveResponseSchema = z.object({
    move: moveSchema,
    /** Remaining moves before the API stops the loop. */
    moves_left: z.int().min(0),
});
/**
 * `POST /extension/jobs/{job_id}/wall-check` (ADR 0036 amended, 21/10): the page detector saw a wall and it is
 * still there after 30 s; one look of the navigation model at the screenshot and a summary of the page: is a
 * challenge blocking the content right now? The model only looks: nothing on the page is touched.
 */
export const wallCheckRequestSchema = z.object({
    job_id: z.string(),
    client_name: z.string().min(1).max(32).optional(),
    url: z.string().max(2048),
    /** What the page detector saw (`captcha_widget`, `browser_check`, `interactive check: …`). */
    signal: z.string().max(500),
    /** The visible text of the page, an excerpt. */
    page_text: z.string().max(4000),
    screenshot_jpeg_base64: z.string().nullable(),
    /** Where navigation stopped; absent for older clients. */
    phase: navigationPhaseSchema.optional(),
    /** The recipe/model action interrupted by the detector, as diagnostic data only. */
    failed_step: z.string().max(500).nullable().optional(),
    last_error: z.string().max(500).nullable().optional(),
});
export const wallDiagnosisSchema = z.enum([
    'clear',
    'passive_check',
    'interactive_challenge',
    'access_denied',
    'unknown',
]);
export const wallCheckResponseSchema = z.object({
    /** True: access is blocked or the diagnostic evidence is insufficient to continue safely. */
    blocking: z.boolean(),
    /** One line: what the model sees. */
    why: z.string().max(500),
    model: z.string(),
    /** Optional for compatibility with older API instances. */
    diagnosis: wallDiagnosisSchema.optional(),
    /** The model's confidence, when supplied; not a calibrated probability. */
    confidence: z.number().min(0).max(1).optional(),
});
//# sourceMappingURL=move.schema.js.map